// src/screens/oauth-callback/OAuthCallback.js
import { useEffect, useState } from 'react';

const OAuthCallback = () => {
  const [closeFailed, setCloseFailed] = useState(false);
  const [status, setStatus] = useState('Processing authorization...');
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const debug = (...args) => console.log('[OAuthCallback Debug]', ...args);

    const searchParams = new URLSearchParams(window.location.search);
    const hashString = window.location.hash?.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash || '';
    const hashParams = new URLSearchParams(hashString);

    const readParam = (...keys) => {
      for (const key of keys) {
        const fromSearch = searchParams.get(key);
        if (fromSearch) return fromSearch;

        const fromHash = hashParams.get(key);
        if (fromHash) return fromHash;
      }
      return '';
    };

    const oauthError = readParam('error');
    const accessToken = readParam('access_token', 'token');
    const refreshToken = readParam('refresh_token');
    const userId = readParam('user_id', 'id');
    const role = readParam('role');
    const userEmail = readParam('email', 'user_email');

    debug('Mounted callback', {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      oauthError,
      hasAccessToken: !!accessToken,
      userId,
      role,
      userEmail,
      hasOpener: !!window.opener,
      origin: window.location.origin,
    });

    const authPayload = {
      source: 'oauth-callback',
      success: true,
      access_token: accessToken,
      refresh_token: refreshToken || '',
      user_id: userId || '',
      role: role || '',
      email: userEmail || '',
      payload: {
        access_token: accessToken,
        refresh_token: refreshToken || '',
        user_id: userId || '',
        role: role || '',
        email: userEmail || '',
      },
    };

    const saveTokensLocally = () => {
      if (!accessToken) {
        debug('saveTokensLocally skipped: no access token');
        return false;
      }

      localStorage.setItem('access_token', accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('user_id', userId || '');
      localStorage.setItem('role', role || '');
      localStorage.setItem('user_email', userEmail || '');

      debug('Saved token locally', {
        access_token: `${accessToken.slice(0, 20)}...`,
        user_id: userId || '',
        role: role || '',
        email: userEmail || '',
      });

      return true;
    };

    const notifyViaBroadcastChannel = (message) => {
      try {
        if (!('BroadcastChannel' in window)) {
          debug('BroadcastChannel not supported');
          return false;
        }

        const channel = new BroadcastChannel('revexia-oauth');
        channel.postMessage(message);
        debug('Posted message via BroadcastChannel', message);
        setTimeout(() => channel.close(), 300);
        return true;
      } catch (err) {
        debug('BroadcastChannel failed', err.message);
        return false;
      }
    };

    const postToOpener = (message) => {
      if (!window.opener) {
        debug('No opener found');
        return false;
      }

      try {
        window.opener.postMessage(message, window.location.origin);
        debug('Posted message to opener', message);
        return true;
      } catch (err) {
        debug('postMessage failed', err.message);
        return false;
      }
    };

    const redirectToAuthPage = (errorMessage = '') => {
      const query = errorMessage ? `?error=${encodeURIComponent(errorMessage)}` : '';
      debug('Redirecting to /auth', { query });

      setTimeout(() => {
        window.location.replace(`/auth${query}`);
      }, 1200);
    };

    const tryCloseWindow = () => {
      setTimeout(() => {
        try {
          debug('Trying window.close()');
          window.close();
        } catch (err) {
          debug('window.close() failed', err.message);
        }
      }, 350);

      setTimeout(() => {
        debug('Window still open; showing manual close UI');
        setCloseFailed(true);
      }, 1200);
    };

    if (oauthError) {
      const decodedError = decodeURIComponent(oauthError);
      setStatus('Authorization failed');
      setIsError(true);
      debug('OAuth error branch', decodedError);

      postToOpener({
        source: 'oauth-callback',
        success: false,
        error: decodedError,
      });

      notifyViaBroadcastChannel({
        source: 'oauth-callback',
        success: false,
        error: decodedError,
      });

      tryCloseWindow();
      redirectToAuthPage(decodedError);
      return;
    }

    if (accessToken) {
      setStatus('Authorization successful!');
      saveTokensLocally();

      postToOpener(authPayload);
      notifyViaBroadcastChannel(authPayload);

      tryCloseWindow();
      redirectToAuthPage();
      return;
    }

    setStatus('No token returned from server');
    setIsError(true);
    debug('No token returned branch');

    const noTokenMessage = {
      source: 'oauth-callback',
      success: false,
      error: 'No token returned from server',
    };

    postToOpener(noTokenMessage);
    notifyViaBroadcastChannel(noTokenMessage);

    tryCloseWindow();
    redirectToAuthPage('No token returned from server');
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#0d0e1a',
        color: '#ffffff',
        fontFamily: 'system-ui, sans-serif',
        flexDirection: 'column',
        gap: '14px',
        padding: '2rem',
      }}
    >
      {!isError ? (
        <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
          <circle
            cx="26"
            cy="26"
            r="24"
            stroke="#22c55e"
            strokeWidth="2.5"
            strokeDasharray="150"
            strokeDashoffset="150"
            style={{ animation: 'drawCircle 0.5s ease forwards' }}
          />
          <path
            d="M15 26l8 8 14-16"
            stroke="#22c55e"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="40"
            strokeDashoffset="40"
            style={{ animation: 'drawCheck 0.4s ease 0.4s forwards' }}
          />
        </svg>
      ) : (
        <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
          <circle cx="26" cy="26" r="24" stroke="#ef4444" strokeWidth="2.5" />
          <path
            d="M18 18l16 16M34 18L18 34"
            stroke="#ef4444"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      )}

      <p
        style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 600,
          color: isError ? '#ef4444' : '#ffffff',
          textAlign: 'center',
        }}
      >
        {status}
      </p>

      <p
        style={{
          margin: 0,
          fontSize: '0.82rem',
          color: '#6B7280',
          textAlign: 'center',
          maxWidth: 320,
        }}
      >
        {closeFailed
          ? 'You can close this window manually and return to the app.'
          : isError
          ? 'Redirecting back to login…'
          : 'This window will close automatically…'}
      </p>

      {closeFailed && (
        <button
          onClick={() => {
            try {
              window.close();
            } catch (err) {
              console.log('[OAuthCallback Debug] Manual close failed', err.message);
            }
          }}
          style={{
            marginTop: '0.5rem',
            padding: '0.55rem 1.4rem',
            background: isError ? '#ef4444' : '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '999px',
            fontSize: '0.8rem',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Close Window
        </button>
      )}

      <style>{`
        @keyframes drawCircle {
          to { stroke-dashoffset: 0; }
        }
        @keyframes drawCheck {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
};

export default OAuthCallback;